import type { Language } from '@order-bot/shared-types';

import { faq } from '@/features/landing/content';

export interface FaqItem {
  q: string;
  a: string;
}

/**
 * The landing page's FAQ, question for question, in each console language. English is the
 * landing page's own list, so the two can't drift; a question added there needs adding here
 * in every language (the test counts them). Menu names inside TallyPrime stay in English, as
 * Tally shows them.
 */
export const FAQ_BY_LANGUAGE: Record<Language, ReadonlyArray<FaqItem>> = {
  en: faq,
  hi: [
    {
      q: 'क्या रिटेलर को ऑर्डर देने का तरीका बदलना पड़ेगा?',
      a: 'नहीं। वे पहले की तरह आपके नंबर पर WhatsApp वॉइस नोट या टेक्स्ट भेजते रहेंगे। उन्हें कन्फर्म करने के लिए ऑर्डर का सार मिलता है, फिर "ऑर्डर कन्फर्म हुआ", और Tally में चढ़ते ही "आपका ऑर्डर बुक हो गया"।',
    },
    {
      q: 'कौन सा Tally चलेगा?',
      a: 'Windows PC पर TallyPrime, जिसमें Tally की कनेक्टिविटी चालू हो (पोर्ट 9000)। ऑर्डर Tally में जाने के लिए वह PC चालू और TallyPrime खुला होना चाहिए; तब तक अप्रूव हुए ऑर्डर सुरक्षित इंतज़ार करते हैं।',
    },
    {
      q: 'क्या कुछ भी हमारे चेक किए बिना Tally में जाता है?',
      a: 'नहीं। हर ऑर्डर रिव्यू क्यू में तब तक रुकता है जब तक आपका स्टाफ उसे अप्रूव न करे। जिन लाइनों पर AI को पूरा भरोसा नहीं होता, उन पर "जाँचें" या "पूछना है" लिखा आता है।',
    },
    {
      q: 'Order Bot को हमारे प्रोडक्ट कैसे पता चलते हैं?',
      a: 'कनेक्टर Tally से आपके स्टॉक आइटम, यूनिट, पैक साइज़ (अल्टरनेट यूनिट) और सेलिंग प्राइस पढ़ता है और उन्हें सिंक रखता है। Tally में डिलीट किए गए आइटम मैच होना बंद हो जाते हैं।',
    },
    {
      q: 'रिटेलर "2 पेटी" बोलता है पर Tally पीस में गिनता है। तब क्या होगा?',
      a: 'Tally का पैक साइज़ इसे बदल देता है: अगर 1 Box = 48 Nos है, तो 2 box बनता है 96 Nos। स्टाफ को हर लाइन पर यह हिसाब दिखता है।',
    },
    {
      q: 'रेट का क्या होता है?',
      a: 'अगर आइटम का Tally में स्टैंडर्ड सेलिंग प्राइस है, तो सेल्स ऑर्डर में वही रेट जाता है। न हो तो ऑर्डर ज़ीरो पर जाता है और रेट इनवॉइस बनाते समय भरा जाता है; इसके लिए Sales Order वाउचर टाइप में "Allow zero-valued transactions" चालू होना चाहिए।',
    },
    {
      q: 'Tally में ऑर्डर किस पार्टी के नाम जाता है?',
      a: 'हर रिटेलर का फ़ोन नंबर "रिटेलर" पेज पर उसके पार्टी लेजर से जोड़ा जाता है, हर रिटेलर के लिए बस एक बार।',
    },
    {
      q: 'Tally या इंटरनेट बंद हो तो?',
      a: 'कनेक्टर अप्रूव हुए ऑर्डर PC पर रखता है और Tally वापस आते ही पोस्ट कर देता है; कोई ऑर्डर दो बार पोस्ट नहीं होता। अगर PC ऑफ़लाइन है और ऑर्डर इंतज़ार कर रहे हैं, तो मालिक को अलर्ट जाता है।',
    },
    {
      q: 'कौन-कौन सी भाषाएं चलती हैं?',
      a: 'हिंदी, अंग्रेज़ी, तेलुगु, कन्नड़, तमिल और बंगाली: WhatsApp मैसेज के लिए भी और स्टाफ कंसोल के लिए भी।',
    },
    {
      q: 'हमारे डेटा का क्या होता है?',
      a: 'वह आपके अकाउंट के साथ रहता है। "आपका डेटा" पेज दिखाता है कि क्या-क्या रखा गया है और किसी रिटेलर का डेटा मिटा भी सकता है। Tally का पोर्ट इंटरनेट पर कभी नहीं खोला जाता: कनेक्टर सिर्फ बाहर की ओर कॉल करता है।',
    },
    {
      q: 'इसे इंस्टॉल कैसे करें?',
      a: '"Tally सेटअप" पेज से कनेक्टर डाउनलोड करें, उसे Tally वाले PC पर चलाएं और पेयरिंग कोड डालें। एडमिनिस्ट्रेटर राइट्स की ज़रूरत नहीं।',
    },
  ],
  te: [
    {
      q: 'రిటైలర్లు ఆర్డర్ చేసే పద్ధతిని మార్చుకోవాలా?',
      a: 'లేదు. వారు ఎప్పటిలాగే మీ నంబర్‌కు వాట్సాప్ వాయిస్ నోట్‌లు లేదా టెక్స్ట్ పంపుతారు. నిర్ధారించడానికి వారికి ఆర్డర్ సారాంశం వస్తుంది, తర్వాత "ఆర్డర్ నిర్ధారించబడింది", అది టాలీలో నమోదైన వెంటనే "మీ ఆర్డర్ బుక్ అయింది" అని వస్తుంది.',
    },
    {
      q: 'ఏ టాలీతో పనిచేస్తుంది?',
      a: 'Windows PCలోని TallyPrime, టాలీ కనెక్టివిటీ ఆన్‌లో ఉండాలి (పోర్ట్ 9000). ఆర్డర్లు టాలీలోకి వెళ్లాలంటే ఆ PC ఆన్‌లో ఉండి TallyPrime తెరిచి ఉండాలి; అప్పటివరకు ఆమోదించిన ఆర్డర్లు సురక్షితంగా వేచి ఉంటాయి.',
    },
    {
      q: 'మేము చెక్ చేయకుండా ఏదైనా టాలీలోకి వెళ్తుందా?',
      a: 'లేదు. మీ సిబ్బంది ఆమోదించే వరకు ప్రతి ఆర్డర్ రివ్యూ క్యూలో వేచి ఉంటుంది. AIకి ఖచ్చితంగా తెలియని లైన్‌లకు "సమీక్ష" లేదా "అడగాలి" అని గుర్తు ఉంటుంది.',
    },
    {
      q: 'Order Botకు మా ఉత్పత్తులు ఎలా తెలుస్తాయి?',
      a: 'కనెక్టర్ టాలీ నుండి మీ స్టాక్ ఐటమ్‌లు, యూనిట్లు, ప్యాక్ సైజ్‌లు (ఆల్టర్నేట్ యూనిట్లు), అమ్మకపు ధరలను చదివి, వాటిని సింక్‌లో ఉంచుతుంది. టాలీలో తొలగించిన ఐటమ్‌లు ఇక మ్యాచ్ కావు.',
    },
    {
      q: 'రిటైలర్ "2 పెట్టెలు" అంటారు, కానీ టాలీ పీస్‌లలో లెక్కిస్తుంది. అప్పుడు ఏమవుతుంది?',
      a: 'టాలీలోని ప్యాక్ సైజ్ దాన్ని మారుస్తుంది: 1 Box = 48 Nos అయితే, 2 box అంటే 96 Nos. ఈ లెక్క సిబ్బందికి ప్రతి లైన్‌లో కనిపిస్తుంది.',
    },
    {
      q: 'ధరల సంగతి ఏమిటి?',
      a: 'ఐటమ్‌కు టాలీలో స్టాండర్డ్ సెల్లింగ్ ప్రైస్ ఉంటే, సేల్స్ ఆర్డర్‌లో అదే ధర ఉంటుంది. లేకపోతే ఆర్డర్ సున్నా విలువతో వెళ్తుంది, ధరను ఇన్వాయిస్ చేసేటప్పుడు నింపుతారు; దీనికి Sales Order వోచర్ టైప్‌లో "Allow zero-valued transactions" ఆన్‌లో ఉండాలి.',
    },
    {
      q: 'టాలీలో ఆర్డర్ ఏ పార్టీ పేరు మీద వెళ్తుంది?',
      a: 'ప్రతి రిటైలర్ ఫోన్ నంబర్‌ను "రిటైలర్లు" పేజీలో దాని పార్టీ లెడ్జర్‌కు లింక్ చేస్తారు, ఒక్కో రిటైలర్‌కు ఒకసారి మాత్రమే.',
    },
    {
      q: 'టాలీ లేదా ఇంటర్నెట్ పనిచేయకపోతే?',
      a: 'కనెక్టర్ ఆమోదించిన ఆర్డర్లను PCలో ఉంచి, టాలీ తిరిగి వచ్చినప్పుడు పోస్ట్ చేస్తుంది; ఏ ఆర్డర్ రెండుసార్లు పోస్ట్ కాదు. ఆర్డర్లు వేచి ఉండగా PC ఆఫ్‌లైన్‌లో ఉంటే యజమానికి అలర్ట్ వస్తుంది.',
    },
    {
      q: 'ఏ భాషలు పనిచేస్తాయి?',
      a: 'హిందీ, ఇంగ్లీష్, తెలుగు, కన్నడ, తమిళం, బెంగాలీ: వాట్సాప్ సందేశాలకు, సిబ్బంది కన్సోల్‌కు రెండింటికీ.',
    },
    {
      q: 'మా డేటా ఏమవుతుంది?',
      a: 'అది మీ ఖాతాతోనే ఉంటుంది. "మీ డేటా" పేజీ ఏమి నిల్వ ఉందో చూపిస్తుంది, రిటైలర్ డేటాను తొలగించగలదు. టాలీ పోర్ట్ ఎప్పుడూ ఇంటర్నెట్‌కు తెరవబడదు: కనెక్టర్ బయటకు మాత్రమే కాల్ చేస్తుంది.',
    },
    {
      q: 'దీన్ని ఎలా ఇన్‌స్టాల్ చేయాలి?',
      a: '"టాలీ సెటప్" పేజీ నుండి కనెక్టర్‌ను డౌన్‌లోడ్ చేసి, టాలీ ఉన్న PCలో రన్ చేసి, పెయిరింగ్ కోడ్ నమోదు చేయండి. అడ్మినిస్ట్రేటర్ హక్కులు అవసరం లేదు.',
    },
  ],
  kn: [
    {
      q: 'ರಿಟೇಲರ್‌ಗಳು ಆರ್ಡರ್ ಮಾಡುವ ವಿಧಾನವನ್ನು ಬದಲಾಯಿಸಬೇಕೇ?',
      a: 'ಇಲ್ಲ. ಅವರು ಎಂದಿನಂತೆ ನಿಮ್ಮ ನಂಬರ್‌ಗೆ ವಾಟ್ಸಾಪ್ ಧ್ವನಿ ಸಂದೇಶ ಅಥವಾ ಪಠ್ಯ ಕಳುಹಿಸುತ್ತಾರೆ. ಖಚಿತಪಡಿಸಲು ಅವರಿಗೆ ಆರ್ಡರ್ ಸಾರಾಂಶ ಬರುತ್ತದೆ, ನಂತರ "ಆರ್ಡರ್ ಖಚಿತವಾಗಿದೆ", ಮತ್ತು ಅದು ಟ್ಯಾಲಿಯಲ್ಲಿ ದಾಖಲಾದ ಕೂಡಲೇ "ನಿಮ್ಮ ಆರ್ಡರ್ ಬುಕ್ ಆಗಿದೆ" ಎಂದು ಬರುತ್ತದೆ.',
    },
    {
      q: 'ಯಾವ ಟ್ಯಾಲಿಯೊಂದಿಗೆ ಕೆಲಸ ಮಾಡುತ್ತದೆ?',
      a: 'Windows PCಯಲ್ಲಿನ TallyPrime, ಟ್ಯಾಲಿಯ ಕನೆಕ್ಟಿವಿಟಿ ಆನ್ ಆಗಿರಬೇಕು (ಪೋರ್ಟ್ 9000). ಆರ್ಡರ್‌ಗಳು ಟ್ಯಾಲಿಗೆ ಹೋಗಲು ಆ PC ಆನ್ ಆಗಿದ್ದು TallyPrime ತೆರೆದಿರಬೇಕು; ಅಲ್ಲಿಯವರೆಗೆ ಅನುಮೋದಿತ ಆರ್ಡರ್‌ಗಳು ಸುರಕ್ಷಿತವಾಗಿ ಕಾಯುತ್ತವೆ.',
    },
    {
      q: 'ನಾವು ಪರಿಶೀಲಿಸದೆ ಏನಾದರೂ ಟ್ಯಾಲಿಗೆ ಹೋಗುತ್ತದೆಯೇ?',
      a: 'ಇಲ್ಲ. ನಿಮ್ಮ ಸಿಬ್ಬಂದಿ ಅನುಮೋದಿಸುವವರೆಗೆ ಪ್ರತಿ ಆರ್ಡರ್ ರಿವ್ಯೂ ಕ್ಯೂನಲ್ಲಿ ಕಾಯುತ್ತದೆ. AIಗೆ ಖಚಿತವಿಲ್ಲದ ಸಾಲುಗಳಿಗೆ "ಪರಿಶೀಲನೆ" ಅಥವಾ "ಕೇಳಬೇಕು" ಎಂದು ಗುರುತು ಇರುತ್ತದೆ.',
    },
    {
      q: 'Order Botಗೆ ನಮ್ಮ ಉತ್ಪನ್ನಗಳು ಹೇಗೆ ಗೊತ್ತಾಗುತ್ತವೆ?',
      a: 'ಕನೆಕ್ಟರ್ ಟ್ಯಾಲಿಯಿಂದ ನಿಮ್ಮ ಸ್ಟಾಕ್ ಐಟಂಗಳು, ಯೂನಿಟ್‌ಗಳು, ಪ್ಯಾಕ್ ಸೈಜ್‌ಗಳು (ಆಲ್ಟರ್ನೇಟ್ ಯೂನಿಟ್‌ಗಳು) ಮತ್ತು ಮಾರಾಟ ಬೆಲೆಗಳನ್ನು ಓದಿ, ಅವುಗಳನ್ನು ಸಿಂಕ್‌ನಲ್ಲಿ ಇಡುತ್ತದೆ. ಟ್ಯಾಲಿಯಲ್ಲಿ ಅಳಿಸಿದ ಐಟಂಗಳು ಇನ್ನು ಮುಂದೆ ಹೊಂದಾಣಿಕೆಯಾಗುವುದಿಲ್ಲ.',
    },
    {
      q: 'ರಿಟೇಲರ್ "2 ಪೆಟ್ಟಿಗೆ" ಎನ್ನುತ್ತಾರೆ, ಆದರೆ ಟ್ಯಾಲಿ ಪೀಸ್‌ಗಳಲ್ಲಿ ಎಣಿಸುತ್ತದೆ. ಆಗ ಏನಾಗುತ್ತದೆ?',
      a: 'ಟ್ಯಾಲಿಯ ಪ್ಯಾಕ್ ಸೈಜ್ ಅದನ್ನು ಪರಿವರ್ತಿಸುತ್ತದೆ: 1 Box = 48 Nos ಆಗಿದ್ದರೆ, 2 box ಎಂದರೆ 96 Nos. ಈ ಲೆಕ್ಕ ಸಿಬ್ಬಂದಿಗೆ ಪ್ರತಿ ಸಾಲಿನಲ್ಲೂ ಕಾಣುತ್ತದೆ.',
    },
    {
      q: 'ಬೆಲೆಗಳ ವಿಷಯ ಏನು?',
      a: 'ಐಟಂಗೆ ಟ್ಯಾಲಿಯಲ್ಲಿ ಸ್ಟ್ಯಾಂಡರ್ಡ್ ಸೆಲ್ಲಿಂಗ್ ಪ್ರೈಸ್ ಇದ್ದರೆ, ಸೇಲ್ಸ್ ಆರ್ಡರ್‌ನಲ್ಲಿ ಅದೇ ಬೆಲೆ ಇರುತ್ತದೆ. ಇಲ್ಲದಿದ್ದರೆ ಆರ್ಡರ್ ಶೂನ್ಯ ಮೌಲ್ಯದಲ್ಲಿ ಹೋಗುತ್ತದೆ ಮತ್ತು ದರವನ್ನು ಇನ್‌ವಾಯ್ಸ್ ಮಾಡುವಾಗ ತುಂಬಲಾಗುತ್ತದೆ; ಇದಕ್ಕೆ Sales Order ವೋಚರ್ ಟೈಪ್‌ನಲ್ಲಿ "Allow zero-valued transactions" ಆನ್ ಆಗಿರಬೇಕು.',
    },
    {
      q: 'ಟ್ಯಾಲಿಯಲ್ಲಿ ಆರ್ಡರ್ ಯಾವ ಪಾರ್ಟಿಯ ಹೆಸರಿಗೆ ಹೋಗುತ್ತದೆ?',
      a: 'ಪ್ರತಿ ರಿಟೇಲರ್‌ನ ಫೋನ್ ನಂಬರ್ ಅನ್ನು "ರಿಟೇಲರ್‌ಗಳು" ಪುಟದಲ್ಲಿ ಅದರ ಪಾರ್ಟಿ ಲೆಡ್ಜರ್‌ಗೆ ಲಿಂಕ್ ಮಾಡಲಾಗುತ್ತದೆ, ಪ್ರತಿ ರಿಟೇಲರ್‌ಗೆ ಒಮ್ಮೆ ಮಾತ್ರ.',
    },
    {
      q: 'ಟ್ಯಾಲಿ ಅಥವಾ ಇಂಟರ್ನೆಟ್ ನಿಂತಿದ್ದರೆ?',
      a: 'ಕನೆಕ್ಟರ್ ಅನುಮೋದಿತ ಆರ್ಡರ್‌ಗಳನ್ನು PCಯಲ್ಲಿ ಇಟ್ಟುಕೊಂಡು, ಟ್ಯಾಲಿ ಮರಳಿ ಬಂದಾಗ ಪೋಸ್ಟ್ ಮಾಡುತ್ತದೆ; ಯಾವ ಆರ್ಡರ್ ಕೂಡ ಎರಡು ಬಾರಿ ಪೋಸ್ಟ್ ಆಗುವುದಿಲ್ಲ. ಆರ್ಡರ್‌ಗಳು ಕಾಯುತ್ತಿರುವಾಗ PC ಆಫ್‌ಲೈನ್ ಆಗಿದ್ದರೆ ಮಾಲೀಕರಿಗೆ ಅಲರ್ಟ್ ಬರುತ್ತದೆ.',
    },
    {
      q: 'ಯಾವ ಭಾಷೆಗಳು ಬೆಂಬಲಿತವಾಗಿವೆ?',
      a: 'ಹಿಂದಿ, ಇಂಗ್ಲಿಷ್, ತೆಲುಗು, ಕನ್ನಡ, ತಮಿಳು ಮತ್ತು ಬಂಗಾಳಿ: ವಾಟ್ಸಾಪ್ ಸಂದೇಶಗಳಿಗೂ ಸಿಬ್ಬಂದಿ ಕನ್ಸೋಲ್‌ಗೂ.',
    },
    {
      q: 'ನಮ್ಮ ಡೇಟಾ ಏನಾಗುತ್ತದೆ?',
      a: 'ಅದು ನಿಮ್ಮ ಖಾತೆಯೊಂದಿಗೇ ಇರುತ್ತದೆ. "ನಿಮ್ಮ ಡೇಟಾ" ಪುಟವು ಏನೆಲ್ಲ ಸಂಗ್ರಹವಾಗಿದೆ ಎಂದು ತೋರಿಸುತ್ತದೆ ಮತ್ತು ರಿಟೇಲರ್‌ನ ಡೇಟಾವನ್ನು ಅಳಿಸಬಲ್ಲದು. ಟ್ಯಾಲಿಯ ಪೋರ್ಟ್ ಅನ್ನು ಎಂದಿಗೂ ಇಂಟರ್ನೆಟ್‌ಗೆ ತೆರೆಯುವುದಿಲ್ಲ: ಕನೆಕ್ಟರ್ ಹೊರಗಡೆಗೆ ಮಾತ್ರ ಕರೆ ಮಾಡುತ್ತದೆ.',
    },
    {
      q: 'ಇದನ್ನು ಇನ್‌ಸ್ಟಾಲ್ ಮಾಡುವುದು ಹೇಗೆ?',
      a: '"ಟ್ಯಾಲಿ ಸೆಟಪ್" ಪುಟದಿಂದ ಕನೆಕ್ಟರ್ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ, ಟ್ಯಾಲಿ ಇರುವ PCಯಲ್ಲಿ ರನ್ ಮಾಡಿ, ಜೋಡಣೆ ಕೋಡ್ ನಮೂದಿಸಿ. ಅಡ್ಮಿನಿಸ್ಟ್ರೇಟರ್ ಹಕ್ಕುಗಳು ಬೇಕಾಗಿಲ್ಲ.',
    },
  ],
  ta: [
    {
      q: 'சில்லறை விற்பனையாளர்கள் ஆர்டர் செய்யும் முறையை மாற்ற வேண்டுமா?',
      a: 'இல்லை. அவர்கள் வழக்கம்போல் உங்கள் எண்ணுக்கு வாட்ஸ்அப் குரல் குறிப்புகள் அல்லது உரையை அனுப்புவார்கள். உறுதிப்படுத்த அவர்களுக்கு ஆர்டர் சுருக்கம் வரும், பிறகு "ஆர்டர் உறுதிசெய்யப்பட்டது", அது டேலியில் பதிவானதும் "உங்கள் ஆர்டர் பதிவு செய்யப்பட்டது" என்று வரும்.',
    },
    {
      q: 'எந்த டேலியுடன் வேலை செய்யும்?',
      a: 'Windows PC-யில் உள்ள TallyPrime; டேலியின் இணைப்பு (connectivity) இயக்கத்தில் இருக்க வேண்டும் (போர்ட் 9000). ஆர்டர்கள் டேலிக்குச் செல்ல அந்த PC இயங்கி, TallyPrime திறந்திருக்க வேண்டும்; அதுவரை அங்கீகரிக்கப்பட்ட ஆர்டர்கள் பாதுகாப்பாகக் காத்திருக்கும்.',
    },
    {
      q: 'நாங்கள் சரிபார்க்காமல் எதுவும் டேலிக்குச் செல்லுமா?',
      a: 'இல்லை. உங்கள் பணியாளர் அங்கீகரிக்கும் வரை ஒவ்வொரு ஆர்டரும் மறுஆய்வு வரிசையில் காத்திருக்கும். AI உறுதியாக இல்லாத வரிகள் "சரிபார்" அல்லது "கேட்க வேண்டும்" என்று குறிக்கப்படும்.',
    },
    {
      q: 'Order Bot-க்கு எங்கள் பொருட்கள் எப்படித் தெரியும்?',
      a: 'கனெக்டர் டேலியிலிருந்து உங்கள் ஸ்டாக் பொருட்கள், அலகுகள், பேக் அளவுகள் (மாற்று அலகுகள்), விற்பனை விலைகளைப் படித்து, அவற்றை ஒத்திசைவில் வைத்திருக்கும். டேலியில் நீக்கப்பட்ட பொருட்கள் இனி பொருத்தப்படாது.',
    },
    {
      q: 'சில்லறை விற்பனையாளர் "2 பெட்டி" என்கிறார், ஆனால் டேலி துண்டுகளாக எண்ணுகிறது. அப்போது என்ன நடக்கும்?',
      a: 'டேலியின் பேக் அளவு அதை மாற்றும்: 1 Box = 48 Nos என்றால், 2 box என்பது 96 Nos. இந்தக் கணக்கு பணியாளர்களுக்கு ஒவ்வொரு வரியிலும் தெரியும்.',
    },
    {
      q: 'விலைகள் பற்றி என்ன?',
      a: 'பொருளுக்கு டேலியில் நிலையான விற்பனை விலை இருந்தால், விற்பனை ஆர்டரில் அதே விலை இருக்கும். இல்லையென்றால் ஆர்டர் பூஜ்ய மதிப்பில் செல்லும், விலை இன்வாய்ஸ் செய்யும்போது நிரப்பப்படும்; இதற்கு Sales Order வவுச்சர் வகையில் "Allow zero-valued transactions" இயக்கத்தில் இருக்க வேண்டும்.',
    },
    {
      q: 'டேலியில் ஆர்டர் எந்தப் பார்ட்டியின் பெயரில் செல்லும்?',
      a: 'ஒவ்வொரு சில்லறை விற்பனையாளரின் தொலைபேசி எண்ணும் "சில்லறை விற்பனையாளர்கள்" பக்கத்தில் அதன் பார்ட்டி லெட்ஜருடன் இணைக்கப்படும்; ஒவ்வொருவருக்கும் ஒருமுறை மட்டுமே.',
    },
    {
      q: 'டேலி அல்லது இணையம் செயலிழந்தால்?',
      a: 'கனெக்டர் அங்கீகரிக்கப்பட்ட ஆர்டர்களை PC-யில் வைத்திருந்து, டேலி மீண்டும் வந்ததும் பதிவிடும்; எந்த ஆர்டரும் இருமுறை பதிவிடப்படாது. ஆர்டர்கள் காத்திருக்கும்போது PC ஆஃப்லைனில் இருந்தால் உரிமையாளருக்கு எச்சரிக்கை வரும்.',
    },
    {
      q: 'எந்த மொழிகள் ஆதரிக்கப்படுகின்றன?',
      a: 'இந்தி, ஆங்கிலம், தெலுங்கு, கன்னடம், தமிழ், வங்காளம்: வாட்ஸ்அப் செய்திகளுக்கும் பணியாளர் கன்சோலுக்கும்.',
    },
    {
      q: 'எங்கள் தரவுக்கு என்ன ஆகும்?',
      a: 'அது உங்கள் கணக்குடனேயே இருக்கும். "உங்கள் தரவு" பக்கம் என்ன சேமிக்கப்பட்டுள்ளது என்பதைக் காட்டும்; ஒரு சில்லறை விற்பனையாளரின் தரவை அழிக்கவும் முடியும். டேலியின் போர்ட் ஒருபோதும் இணையத்திற்குத் திறக்கப்படாது: கனெக்டர் வெளியே மட்டுமே அழைக்கும்.',
    },
    {
      q: 'இதை எப்படி நிறுவுவது?',
      a: '"டேலி அமைப்பு" பக்கத்திலிருந்து கனெக்டரைப் பதிவிறக்கி, டேலி உள்ள PC-யில் இயக்கி, இணைப்பு குறியீட்டை உள்ளிடவும். நிர்வாகி உரிமைகள் தேவையில்லை.',
    },
  ],
  bn: [
    {
      q: 'রিটেইলারদের কি অর্ডার দেওয়ার ধরন বদলাতে হবে?',
      a: 'না। তারা আগের মতোই আপনার নম্বরে হোয়াটসঅ্যাপ ভয়েস নোট বা টেক্সট পাঠাবে। নিশ্চিত করার জন্য তারা অর্ডারের সারাংশ পায়, তারপর "অর্ডার নিশ্চিত হয়েছে", আর ট্যালিতে উঠলেই "আপনার অর্ডার বুক হয়েছে"।',
    },
    {
      q: 'কোন ট্যালিতে চলবে?',
      a: 'Windows PC-তে TallyPrime, যেখানে ট্যালির কানেক্টিভিটি চালু আছে (পোর্ট 9000)। অর্ডার ট্যালিতে যেতে হলে ওই PC চালু এবং TallyPrime খোলা থাকতে হবে; ততক্ষণ অনুমোদিত অর্ডার নিরাপদে অপেক্ষা করে।',
    },
    {
      q: 'আমরা চেক না করলে কি কিছু ট্যালিতে যায়?',
      a: 'না। আপনার স্টাফ অনুমোদন না করা পর্যন্ত প্রতিটি অর্ডার রিভিউ কিউতে অপেক্ষা করে। যে লাইনগুলো নিয়ে AI নিশ্চিত নয়, সেগুলোতে "যাচাই" বা "জিজ্ঞাসা" চিহ্ন থাকে।',
    },
    {
      q: 'Order Bot আমাদের পণ্য কীভাবে জানে?',
      a: 'কানেক্টর ট্যালি থেকে আপনার স্টক আইটেম, ইউনিট, প্যাক সাইজ (অল্টারনেট ইউনিট) ও বিক্রয়মূল্য পড়ে এবং সেগুলো সিঙ্কে রাখে। ট্যালিতে মুছে ফেলা আইটেম আর মেলানো হয় না।',
    },
    {
      q: 'রিটেইলার বলে "2 পেটি", কিন্তু ট্যালি পিস হিসেবে গোনে। তখন কী হয়?',
      a: 'ট্যালির প্যাক সাইজ সেটি বদলে দেয়: 1 Box = 48 Nos হলে 2 box হয় 96 Nos। স্টাফ প্রতিটি লাইনে এই হিসাব দেখতে পায়।',
    },
    {
      q: 'দামের কী হবে?',
      a: 'আইটেমের ট্যালিতে স্ট্যান্ডার্ড সেলিং প্রাইস থাকলে সেলস অর্ডারে সেই দামই যায়। না থাকলে অর্ডার শূন্য মূল্যে যায় এবং দর ইনভয়েস করার সময় বসানো হয়; এর জন্য Sales Order ভাউচার টাইপে "Allow zero-valued transactions" চালু থাকতে হবে।',
    },
    {
      q: 'ট্যালিতে অর্ডার কোন পার্টির নামে যায়?',
      a: 'প্রতিটি রিটেইলারের ফোন নম্বর "রিটেইলার" পেজে তার পার্টি লেজারের সাথে যুক্ত করা হয়, প্রতি রিটেইলারের জন্য একবারই।',
    },
    {
      q: 'ট্যালি বা ইন্টারনেট বন্ধ থাকলে?',
      a: 'কানেক্টর অনুমোদিত অর্ডার PC-তে রেখে দেয় এবং ট্যালি ফিরলে পোস্ট করে; কোনো অর্ডার দুবার পোস্ট হয় না। অর্ডার অপেক্ষায় থাকা অবস্থায় PC অফলাইন থাকলে মালিক একটি অ্যালার্ট পান।',
    },
    {
      q: 'কোন কোন ভাষা চলে?',
      a: 'হিন্দি, ইংরেজি, তেলুগু, কন্নড়, তামিল ও বাংলা: হোয়াটসঅ্যাপ মেসেজ এবং স্টাফ কনসোল, দুটোর জন্যই।',
    },
    {
      q: 'আমাদের ডেটার কী হয়?',
      a: 'তা আপনার অ্যাকাউন্টের সাথেই থাকে। "আপনার ডেটা" পেজ দেখায় কী কী রাখা আছে এবং কোনো রিটেইলারের ডেটা মুছে দিতে পারে। ট্যালির পোর্ট কখনও ইন্টারনেটে খোলা হয় না: কানেক্টর শুধু বাইরের দিকে কল করে।',
    },
    {
      q: 'এটি কীভাবে ইনস্টল করব?',
      a: '"ট্যালি সেটআপ" পেজ থেকে কানেক্টর ডাউনলোড করুন, ট্যালি থাকা PC-তে চালান এবং পেয়ারিং কোড দিন। অ্যাডমিনিস্ট্রেটর অধিকার লাগে না।',
    },
  ],
};
